import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronUp } from "react-ikonate";
import { motion } from "framer-motion";

import styles from "./SelectDropdown.module.css";
import classNames from "classnames";

export function SelectDropdown({
  placeholder,
  options,
  onChange,
  disabled,
  dropdownRight = false,
  className,
  isScrollable,
}) {
  const [dropdownDisplay, changeDropDownDisplay] = useState(false);
  const [selectedOption, setSelectedOption] = useState(placeholder);
  const shouldHideUpChevron = className && className.includes("hideUpChevron");

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          changeDropDownDisplay(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useEffect(() => {
    setSelectedOption(placeholder);
  }, [placeholder]);

  return (
    <div ref={wrapperRef} className={styles.container}>
      <button
        type="button"
        className={classNames(
          styles.selectDropdown,
          className,
          dropdownDisplay ? styles.selectDropdownOpened : styles.selectDropdown
        )}
        onClick={() => changeDropDownDisplay(!dropdownDisplay)}
        disabled={disabled}
      >
        <span className={styles.placeholder}>{selectedOption}</span>
        <span className={styles.selectChevron}>
          {!shouldHideUpChevron && <ChevronUp fontSize="1em" />}
          <ChevronDown fontSize="1em" />
        </span>
      </button>
      {dropdownDisplay && (
        <motion.div
          className={classNames(
            dropdownRight ? styles.selectOptionsRight : styles.selectOptions,
            { [styles.scrollable]: isScrollable }
          )}
          initial={{ scaleY: 0.85, y: -6 }}
          animate={{ scaleY: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {options.map((option) => (
            <button
              type="button"
              className={styles.selectMenuOptions}
              key={option}
              onClick={() => {
                setSelectedOption(option);
                onChange(option);
                changeDropDownDisplay(false);
              }}
            >
              {option}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
}

SelectDropdown.propTypes = {
  /**
   * String that contains the default value shown in dropdown
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Array of options for the dropdown
   */
  options: PropTypes.array.isRequired,
  /**
   * Function that handles the changes of option
   */
  onChange: PropTypes.func.isRequired,
  /**
   * A boolean to determine if it’s disabled or not.
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * A string that can alter style
   */
  className: PropTypes.string,
  /**
   * A boolean to determine if it can be scrolled or not.
   */
  isScrollable: PropTypes.bool,
};
